import { Component, ErrorHandler, Input, OnDestroy, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'

import { ValidatorFn } from '@angular/forms'
import { applyEach, disabled, FieldTree, form, required, validate } from '@angular/forms/signals'

import { KayttajaService } from 'app/_angular/service/kayttaja.service'
import { asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from 'app/_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { LemonTranslationService } from 'app/_jaettu-angular/service/lemon-translation.service'

import { CustomerHallituksenJasenRole, Tilikausi } from '@lemon/core-firebase/model/kayttaja'
import {
  DownloadHallituksenPoytakirjaPdfRequest,
  DownloadLuetteloKirjanpidoistaPdfRequest,
  DownloadLuetteloKirjanpidoistaPdfResponse,
  DownloadTilinpaatosPdfRequest,
  DownloadTilinpaatosPdfResponse,
  SaveTilinpaatosSignersRequest,
  SaveTilinpaatosSignersResponse,
  SignTilinpaatosRequest,
  SignTilinpaatosResponse,
  TilinpaatosHallitusMultiRoleUser,
  TilinpaatosHallitusUser,
  TilinpaatosMetadata,
  TilinpaatosSigningData,
  TilinpaatosUserData,
} from '@lemon/core-firebase/model/tilinpaatos'

import { PdfService } from 'app/_angular/service/pdf.service'
import { BehaviorSubject, combineLatest, firstValueFrom, map, Observable, of as observableOf, Subject, switchMap, takeUntil } from 'rxjs'

import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'

import { MatDialog } from '@angular/material/dialog'
import { TilinpaatosUriService } from '@lemon/core-firebase/service/tilinpaatos-uri.service'
import { NumberDate, Timestamp } from '@lemon/core/model/common'
import { DocumentSnapshot } from 'firebase/firestore'
import { lemonShare } from '../_jaettu-angular/_rxjs/lemon-share.operator'
import { ImageCacheClass, ImageCacheClassPreRendered } from './tilinpaatokset.component'
import { TilinpaatosAreYouSureDialog } from './tilinpaatos-are-you-sure.dialog'
import { TilinpaatosSignedDialog, TilinpaatosSignedDialogData } from './tilinpaatos-signed.dialog'

import { DomSanitizer } from '@angular/platform-browser'
import { DateService } from '@lemon/core/service/date.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonaid.service'
import { FormValidators } from 'app/_jaettu-angular/_validators/FormValidators'
import { FileSaverService } from 'app/_jaettu-angular/service/file-saver'

interface ViewData {
  tilinpaatosHasBeenExported: boolean
  mode: 'edit' | 'view' | 'loading'
  tpPages: ImageCacheClass[]
  luetteloKirjanpidoistaPages: ImageCacheClass[]
  ptkirjaPages: ImageCacheClass[]
  signed: boolean
  startPdfImageRendering: () => Promise<void>
}

interface TilinpaatosHallitusUserPlusSigningStatus extends TilinpaatosHallitusMultiRoleUser {
  /** Concatenate roles into one string! */
  rolesLocalized: string
  signedAt: Timestamp
  isCurrentUser: boolean
}

interface HallituksenJasenLomake {
  name: string
  role: CustomerHallituksenJasenRole
  lemonaidUid: string | null
}

interface TilinpaatosLomake {
  boardMembers: HallituksenJasenLomake[]
  boardMinutesDate: Date | null
  boardMinutesNumber: string
}

@Component({
  selector: 'app-reports-tilinpaatos',
  templateUrl: './tilinpaatos.component.html',
  styleUrls: ['./tilinpaatokset.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TilinpaatosComponent implements OnInit, OnDestroy {
  @Input() selectedTilikausiObservable: Observable<Tilikausi>
  @Input() tilinpaatosUsersObservable: Observable<TilinpaatosUserData>
  @Input() tilinpaatosSnapshotObservable: Observable<{ tilikausi: Tilikausi; snap: DocumentSnapshot<TilinpaatosMetadata> }>

  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  private _saveStarted: boolean = false

  readonly commonError = signal<string>(undefined)
  private readonly _poytakirjanPaivamaaranValidaattori = signal<ValidatorFn>(() => null)
  lomake = form(signal<TilinpaatosLomake>({ boardMembers: [], boardMinutesDate: null, boardMinutesNumber: '' }), polku => {
    validate(polku.boardMembers, ({ value }) => (value().length < 1 ? { kind: 'min' } : null))
    applyEach(polku.boardMembers, jasen => {
      disabled(jasen.lemonaidUid)
      disabled(jasen, ({ valueOf }) => !valueOf(jasen.lemonaidUid))
    })
    validoiReaktiivisesti(polku.boardMinutesDate, kontrolli => this._poytakirjanPaivamaaranValidaattori()(kontrolli))
    required(polku.boardMinutesDate)
    required(polku.boardMinutesNumber)
  })
  tilinpaatosSignersHaveBeenApproved: boolean
  approvedSignersObservable: Observable<TilinpaatosHallitusUserPlusSigningStatus[]>
  allBoardMemberRolesObservable: Observable<{ name: string; key: CustomerHallituksenJasenRole }[]>
  tilinpaatosViewDataObservable: Observable<ViewData>

  private _tilinpaatosMetadataObservable: Observable<TilinpaatosMetadata>
  private _tilinpaatosSigningDataObservable: Observable<TilinpaatosSigningData>
  private _tilinpaatosPdfAsBase64Observable: Observable<{ base64: string; tilikausi: Tilikausi }>
  private _luetteloKirjanpidoistaPdfObservable: Observable<{ base64: string; tilikausi: Tilikausi }>
  private _hallituksenPoytakirjaPdfObservable: Observable<{ base64: string; tilikausi: Tilikausi }>
  private _editingSignersSubject: BehaviorSubject<'edit' | 'view' | 'loading'> = new BehaviorSubject(null)

  tilinpaatosSigningDisabledObservable: Observable<boolean>
  tilinpaatosMissingSignaturesObservable: Observable<boolean>

  constructor(
    private _kayttajaService: KayttajaService,
    private _translationService: LemonTranslationService,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _errorHandler: ErrorHandler,
    private _validationService: FormValidationService,
    private _ladataanService: LadataanService,
    private _pdfService: PdfService,
    private _tilinpaatosUriService: TilinpaatosUriService,
    private _dialog: MatDialog,
    private _domSanitizer: DomSanitizer,
    private _dateService: DateService,
    private _fileSaverService: FileSaverService,
  ) {}

  ngOnInit() {
    this._tilinpaatosMetadataObservable = this.tilinpaatosSnapshotObservable.pipe(map(snap => snap?.snap?.data() || null))

    const tilinpaatosSigningDataDocObservable = combineLatest([this._tilinpaatosMetadataObservable, this._kayttajaService.kayttajanTiedotObservable]).pipe(
      switchMap(([metadata, kayttaja]) => {
        if (!metadata || !kayttaja) {
          return observableOf<DocumentSnapshot<TilinpaatosSigningData>>(null)
        }
        return this._firebaseLemonaid
          .firestoreDoc<TilinpaatosSigningData>(this._tilinpaatosUriService.getTilinpaatosSignaturesDocUri(kayttaja.asiakasAvain, metadata.viimeisinTilinpaatosVersioAvain))
          .listenSnap()
      }),
    )

    this._tilinpaatosSigningDataObservable = tilinpaatosSigningDataDocObservable.pipe(map(snap => snap?.data() ?? null))

    this.tilinpaatosMissingSignaturesObservable = this._tilinpaatosSigningDataObservable.pipe(
      map(signingData => {
        if (!signingData?.signs?.length) {
          return true
        }
        return signingData.signs.filter(sign => !sign.signedAt).length > 0
      }),
    )

    this._tilinpaatosPdfAsBase64Observable = combineLatest([this.tilinpaatosSnapshotObservable, this._translationService.currentLanguageObservable]).pipe(
      switchMap(([metadataDoc, lang]) => {
        if (!metadataDoc?.tilikausi || !lang) {
          return observableOf<{ tilikausi: Tilikausi; base64: string }>(null)
        }
        if (!metadataDoc?.snap?.exists()) {
          return observableOf<{ tilikausi: Tilikausi; base64: string }>({ tilikausi: metadataDoc.tilikausi, base64: null })
        }
        const overrideLang = 'fi' // TODO! Remove once ENG translation is live
        const metadata = metadataDoc.snap.data()
        const reqData: DownloadTilinpaatosPdfRequest = {
          uri: metadata.tilinpaatosPdfienUrit[metadata.viimeisinTilinpaatosVersioAvain + '_' + overrideLang],
        }
        return this._firebaseLemonaid.functionsCall<DownloadTilinpaatosPdfRequest, DownloadTilinpaatosPdfResponse>('tilinpaatosPdfDownload', reqData).then(resp => {
          if (!resp) {
            return { tilikausi: metadataDoc.tilikausi, base64: null }
          }
          if (resp.e) {
            this._errorHandler.handleError(new Error('Tilinpäätös PDF download failed! ' + resp.e))
            return { tilikausi: metadataDoc.tilikausi, base64: null }
          }
          return { tilikausi: metadataDoc.tilikausi, base64: resp.base64Pdf }
        })
      }),
    )

    const tilinpaatosPdfPagesObservable = this._tilinpaatosPdfAsBase64Observable.pipe(
      switchMap(async base64Data => {
        if (!base64Data || !base64Data.tilikausi) {
          return null
        }
        if (!base64Data.base64) {
          return { tilikausi: base64Data.tilikausi, pages: [] }
        }
        const arrayBuffer = this._fileSaverService.base64StringToUint8Array(base64Data.base64)
        const openData = await this._pdfService.isPdfOpenableRenderAllToObjectUrl(arrayBuffer, 720)
        if (openData?.success) {
          const sivut: ImageCacheClass[] = []
          for (const page of openData.renderedPages) {
            // Create firestore tosite image metadata
            sivut.push(new ImageCacheClassPreRendered(this._domSanitizer.bypassSecurityTrustUrl(page) as string))
          }
          return { tilikausi: base64Data.tilikausi, pages: sivut }
        }
        // const openData = await this._pdfService.isPdfOpenableObjectUrl(arrayBuffer, 720)
        // if (openData?.success) {
        //   const sivut: ImageCacheClass[] = [new ImageCacheClassPreRendered(this._domSanitizer.bypassSecurityTrustUrl(openData.firstPageAsImage) as string)]
        //   for (let kuvanIndeksi = 2; kuvanIndeksi <= openData.pageCount; kuvanIndeksi++) {
        //     // Create firestore tosite image metadata
        //     sivut.push(new ImageCacheClassPdfiumRenderer(this._pdfService, openData, this._domSanitizer, kuvanIndeksi))
        //   }
        //   return sivut
        // }
        return { tilikausi: base64Data.tilikausi, pages: [] }
      }),
    )

    this.allBoardMemberRolesObservable = this._translationService.currentLanguageObservable.pipe(
      map(lang => {
        if (!lang) {
          return []
        }
        return [
          { name: this._translationService.lokalisoiKielella('lemonaid-sopimus-dialog.kyc.toimitusjohtaja', lang), key: 'toimitusjohtaja' },
          { name: this._translationService.lokalisoiKielella('lemonaid-sopimus-dialog.kyc.hallituksen-jasen', lang), key: 'jasen' },
          { name: this._translationService.lokalisoiKielella('lemonaid-sopimus-dialog.kyc.hallituksen-puheenjohtaja', lang), key: 'puheenjohtaja' },
          { name: this._translationService.lokalisoiKielella('lemonaid-sopimus-dialog.kyc.hallituksen-varajasen', lang), key: 'varajasen' },
        ]
      }),
    )

    this.approvedSignersObservable = combineLatest([this._tilinpaatosSigningDataObservable, this.allBoardMemberRolesObservable, this._kayttajaService.kayttajanTiedotObservable]).pipe(
      map(([signingData, allRolesLocalized, kayttaja]) => {
        if (!kayttaja || !allRolesLocalized || !signingData?.signs?.length) {
          return []
        }
        const output: TilinpaatosHallitusUserPlusSigningStatus[] = []
        for (const signing of signingData.signs) {
          const user: TilinpaatosHallitusUserPlusSigningStatus = {
            lemonaidUid: signing.user.lemonaidUid,
            name: signing.user.name,
            roles: signing.user.roles,
            rolesLocalized: signing.user.roles.map(role => allRolesLocalized.find(localized => localized.key === role)?.name || '').join('\n'),
            signedAt: signing.signedAt,
            isCurrentUser: kayttaja.uid === signing.user.lemonaidUid,
          }
          output.push(user)
        }
        return output
      }),
      lemonShare(),
    )

    this._hallituksenPoytakirjaPdfObservable = combineLatest([this.tilinpaatosSnapshotObservable, this._tilinpaatosSigningDataObservable, this._translationService.currentLanguageObservable]).pipe(
      switchMap(([metadataDoc, signingData, lang]) => {
        if (!metadataDoc || !metadataDoc.tilikausi || !lang) {
          return observableOf<{ tilikausi: Tilikausi; base64: string }>(null)
        }

        if (!metadataDoc.snap?.exists()) {
          return observableOf<{ tilikausi: Tilikausi; base64: string }>({ tilikausi: metadataDoc.tilikausi, base64: null })
        }

        // Check has dropped at some point. It is essential to have the check
        // As it prevents users from seeing the preview of "Hallituksen kokous"
        // PDF that should NOT be displayed if the tilinpäätös has been signed
        // outside of our system.
        if (!signingData?.boardMinutesNumber) {
          return observableOf<{ tilikausi: Tilikausi; base64: string }>({ tilikausi: metadataDoc.tilikausi, base64: null })
        }

        // This check is a second check for the above.
        const metadata = metadataDoc.snap.data()
        if (metadata.tilinpaatosAllekirjoitettuUlkopuolella) {
          return observableOf<{ tilikausi: Tilikausi; base64: string }>({ tilikausi: metadataDoc.tilikausi, base64: null })
        }

        if (!metadata.hallituksenPoytakirjaPdfienUrit) {
          return observableOf<{ tilikausi: Tilikausi; base64: string }>({ tilikausi: metadataDoc.tilikausi, base64: null })
        }

        const overrideLang = 'fi' // TODO! Remove once ENG translation is live

        const reqData: DownloadHallituksenPoytakirjaPdfRequest = {
          uri: metadata.hallituksenPoytakirjaPdfienUrit[metadata.viimeisinTilinpaatosVersioAvain + '_' + overrideLang],
        }
        return this._firebaseLemonaid.functionsCall<DownloadHallituksenPoytakirjaPdfRequest, DownloadTilinpaatosPdfResponse>('hallituksenPoytakirjaPdfDownload', reqData).then(resp => {
          if (!resp) {
            return { tilikausi: metadataDoc.tilikausi, base64: null }
          }
          if (resp.e) {
            this._errorHandler.handleError(new Error('Hallituksen pöytäkirja PDF download failed! ' + resp.e))
            return { tilikausi: metadataDoc.tilikausi, base64: null }
          }
          return { tilikausi: metadataDoc.tilikausi, base64: resp.base64Pdf }
        })
      }),
    )

    const hallituksenPoytakirjaPdfPagesObservable: Observable<{ pages: ImageCacheClass[]; tilikausi: Tilikausi }> = this._hallituksenPoytakirjaPdfObservable.pipe(
      switchMap(async base64Data => {
        if (!base64Data || !base64Data.tilikausi) {
          return null
        }
        if (!base64Data.base64) {
          return { tilikausi: base64Data.tilikausi, pages: [] }
        }
        const arrayBuffer = this._fileSaverService.base64StringToUint8Array(base64Data.base64)
        const openData = await this._pdfService.isPdfOpenableRenderAllToObjectUrl(arrayBuffer, 720)
        if (openData?.success) {
          const sivut: ImageCacheClass[] = []
          for (const page of openData.renderedPages) {
            // Create firestore tosite image metadata
            sivut.push(new ImageCacheClassPreRendered(this._domSanitizer.bypassSecurityTrustUrl(page) as string))
          }
          return { tilikausi: base64Data.tilikausi, pages: sivut }
        }
        // const openData = await this._pdfService.isPdfOpenableObjectUrl(arrayBuffer, 720)
        // if (openData?.success) {
        //   const sivut: ImageCacheClass[] = [new ImageCacheClassPreRendered(this._domSanitizer.bypassSecurityTrustUrl(openData.firstPageAsImage) as string)]
        //   for (let kuvanIndeksi = 2; kuvanIndeksi <= openData.pageCount; kuvanIndeksi++) {
        //     // Create firestore tosite image metadata
        //     sivut.push(new ImageCacheClassPdfiumRenderer(this._pdfService, openData, this._domSanitizer, kuvanIndeksi))
        //   }
        //   return sivut
        // }
        return { tilikausi: base64Data.tilikausi, pages: [] }
      }),
    )

    this._luetteloKirjanpidoistaPdfObservable = combineLatest([
      this.tilinpaatosSnapshotObservable,
      this._translationService.currentLanguageObservable,
      this._kayttajaService.nykyinenAsiakasObservable,
    ]).pipe(
      switchMap(([metadataDoc, lang, asiakas]) => {
        if (!metadataDoc || !metadataDoc.tilikausi || !lang) {
          return observableOf<{ tilikausi: Tilikausi; base64: string }>(null)
        }

        if (!metadataDoc.snap?.exists()) {
          return observableOf<{ tilikausi: Tilikausi; base64: string }>({ tilikausi: metadataDoc.tilikausi, base64: null })
        }

        const overrideLang = 'fi' // TODO! Remove once ENG translation is live

        const metadata = metadataDoc.snap.data()
        if (!metadata.luetteloKirjanpidoistaPdfienUrit) {
          return observableOf<{ tilikausi: Tilikausi; base64: string }>({ tilikausi: metadataDoc.tilikausi, base64: null })
        }

        const reqData: DownloadLuetteloKirjanpidoistaPdfRequest = {
          uri: metadata.luetteloKirjanpidoistaPdfienUrit[metadata.viimeisinTilinpaatosVersioAvain + '_' + overrideLang],
        }

        return this._firebaseLemonaid.functionsCall<DownloadLuetteloKirjanpidoistaPdfRequest, DownloadLuetteloKirjanpidoistaPdfResponse>('luetteloKirjanpidoistaPdfDownload', reqData).then(resp => {
          if (!resp) {
            return { tilikausi: metadataDoc.tilikausi, base64: null }
          }
          if (resp.e) {
            this._errorHandler.handleError(new Error('Luettelo kirjanpidoista PDF download failed! ' + resp.e))
            return { tilikausi: metadataDoc.tilikausi, base64: null }
          }
          return { tilikausi: metadataDoc.tilikausi, base64: resp.base64Pdf }
        })
      }),
    )

    const luetteloKirjanpidoistaPdfPagesObservable: Observable<{ pages: ImageCacheClass[]; tilikausi: Tilikausi }> = this._luetteloKirjanpidoistaPdfObservable.pipe(
      switchMap(async base64Data => {
        if (!base64Data || !base64Data.tilikausi) {
          return null
        }
        if (!base64Data.base64) {
          return { tilikausi: base64Data.tilikausi, pages: [] }
        }
        const arrayBuffer = this._fileSaverService.base64StringToUint8Array(base64Data.base64)
        const openData = await this._pdfService.isPdfOpenableRenderAllToObjectUrl(arrayBuffer, 720)
        if (openData?.success) {
          const sivut: ImageCacheClass[] = []
          for (const page of openData.renderedPages) {
            // Create firestore tosite image metadata
            sivut.push(new ImageCacheClassPreRendered(this._domSanitizer.bypassSecurityTrustUrl(page) as string))
          }
          return { tilikausi: base64Data.tilikausi, pages: sivut }
        }
        // const openData = await this._pdfService.isPdfOpenableObjectUrl(arrayBuffer, 720)
        // if (openData?.success) {
        //   const sivut: ImageCacheClass[] = [new ImageCacheClassPreRendered(this._domSanitizer.bypassSecurityTrustUrl(openData.firstPageAsImage) as string)]
        //   for (let kuvanIndeksi = 2; kuvanIndeksi <= openData.pageCount; kuvanIndeksi++) {
        //     // Create firestore tosite image metadata
        //     sivut.push(new ImageCacheClassPdfiumRenderer(this._pdfService, openData, this._domSanitizer, kuvanIndeksi))
        //   }
        //   return sivut
        // }
        return { tilikausi: base64Data.tilikausi, pages: [] }
      }),
    )

    // const poytakirjaViewDataObservable = combineLatest([hallituksenPoytakirjaPdfPagesObservable, this._editingSignersSubject]).pipe(
    //   map(([pdfPages, editing]) => {
    //     if (!pdfPages) {
    //       return null
    //     }
    //     const viewData: ViewData = {
    //       mode: editing,
    //       pdfPages: pdfPages,
    //       signed: true,
    //       tilinpaatosHasBeenExported: null,
    //       startPdfImageRendering: async () => {

    //         // NOT NEEDED AS EVERYTHING IS RENDERED AT ONE GO!
    //         // await this._sleep(300)
    //         // for (const page of pdfPages) {
    //         //   await page.startImageRendering()
    //         //   await this._sleep(50)
    //         // }
    //       }
    //     }
    //     viewData.startPdfImageRendering()
    //     return viewData
    //   })
    // )

    this.tilinpaatosViewDataObservable = combineLatest([
      this.tilinpaatosSnapshotObservable,
      tilinpaatosPdfPagesObservable,
      luetteloKirjanpidoistaPdfPagesObservable,
      hallituksenPoytakirjaPdfPagesObservable,
      this._editingSignersSubject,
    ]).pipe(
      map(([metadataDoc, tpPages, luetteloKirjanpidoistaPages, ptkirjaPages, editing]) => {
        if (!metadataDoc?.tilikausi || !metadataDoc?.snap || !tpPages?.tilikausi || !luetteloKirjanpidoistaPages?.tilikausi || !ptkirjaPages?.tilikausi || !tpPages?.pages || !ptkirjaPages?.pages) {
          return null
        }

        // If tilikaudet don't match we'd show inconsistent state in ui
        // this check is here to wait that all relevant data is loaded
        // for the same tilikausi.
        if (
          metadataDoc.tilikausi.avain !== tpPages.tilikausi.avain ||
          metadataDoc.tilikausi.avain !== luetteloKirjanpidoistaPages.tilikausi.avain ||
          metadataDoc.tilikausi.avain !== ptkirjaPages.tilikausi.avain
        ) {
          return null
        }

        const metadata = metadataDoc.snap.data()
        const viewData: ViewData = {
          mode: editing,
          tpPages: tpPages.pages,
          luetteloKirjanpidoistaPages: luetteloKirjanpidoistaPages.pages,
          ptkirjaPages: ptkirjaPages.pages,
          signed: !!metadata?.pdfsAreSigned,
          tilinpaatosHasBeenExported: metadataDoc.snap.exists(),
          startPdfImageRendering: async () => {
            // NOT NEEDED AS EVERYTHING IS RENDERED AT ONE GO!
            // await this._sleep(300)
            // for (const page of pdfPages) {
            //   await page.startImageRendering()
            //   await this._sleep(50)
            // }
          },
        }
        // viewData.startPdfImageRendering()
        return viewData
      }),
    )

    // this.tilinpaatosPdfSignedAndUpdatedObservable = this.tilinpaatosSnapshotObservable.pipe(
    //   map(metadataDoc => {
    //     if (!metadataDoc) {
    //       return null
    //     }
    //     const metadata = metadataDoc.data()
    //     return { signed: !!metadata?.pdfsAreSigned }
    //   })
    // )

    // this.tilinpaatosHasBeenExportedObservable = this.tilinpaatosSnapshotObservable.pipe(
    //   map(metadataSnap => {
    //     if (metadataSnap) {
    //       const res: HasBeenExported = {
    //         exported: metadataSnap.exists()
    //       }
    //       return res
    //     }
    //     return null
    //   })
    // )

    this._tilinpaatosMetadataObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(metadata => {
      const currentVersionCreatedAt = metadata?.tilinpaatosVersioidenAikaleimat[metadata?.viimeisinTilinpaatosVersioAvain]
      this._poytakirjanPaivamaaranValidaattori.set(FormValidators.minDateValidator(this._dateService.lisaaPaivia(currentVersionCreatedAt?.toDate() ?? new Date(), -1)))
    })

    const boardMembersObservable: Observable<TilinpaatosHallitusUser[]> = this.tilinpaatosUsersObservable.pipe(
      map(kyc => {
        return kyc?.hallitusUsers ?? []
      }),
    )

    boardMembersObservable
      .pipe(
        map(boardMembers => {
          if (!boardMembers?.length) {
            return []
          }
          return boardMembers.map(member => {
            const multiRoleUser: { name: string; roles: CustomerHallituksenJasenRole[]; lemonaidUid: string } = {
              name: member.etunimi + ' ' + member.sukunimi,
              roles: [member.role],
              lemonaidUid: member.lemonaidUid,
            }
            return multiRoleUser
          })
        }),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(signers => {
        const jasenet: HallituksenJasenLomake[] = []
        for (const signer of signers) {
          for (const role of signer.roles) {
            jasenet.push(this._luoHallituksenJasen(signer, role))
          }
        }
        asetaArvo(this.lomake.boardMembers, jasenet)
      })

    this.tilinpaatosSigningDisabledObservable = boardMembersObservable.pipe(
      map(members => {
        return !!members.find(member => member.role !== 'varajasen' && !member.lemonaidUid)
      }),
    )

    tilinpaatosSigningDataDocObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(signingDoc => {
      if (!signingDoc) {
        // No doc, still loading
        this._editingSignersSubject.next('loading')
        return
      }

      // Ok, we have a firestore lib doc
      const signingData = signingDoc.data()
      if (signingData) {
        // Ok, we have a document in the firestore database
        const signCount = signingData.signs?.length ?? 0 // How many signature positions are configured?
        this._editingSignersSubject.next(signCount ? 'view' : 'edit')
        asetaArvo(this.lomake.boardMinutesDate, this._dateService.numberToDate(signingData.boardMinutesDate) ?? new Date())
        asetaArvo(this.lomake.boardMinutesNumber, signingData.boardMinutesNumber ?? '1/' + new Date().getFullYear())
      } else {
        // No data in datbase - default to edit
        this._editingSignersSubject.next('edit')
        asetaArvo(this.lomake.boardMinutesDate, new Date())
        asetaArvo(this.lomake.boardMinutesNumber, '1/' + new Date().getFullYear())
      }
    })
  }

  protected _sleep(millis: number): Promise<any> {
    return new Promise(resolve => {
      setTimeout(resolve, millis)
    })
  }

  editSigners() {
    this._editingSignersSubject.next('edit')
  }

  private _luoHallituksenJasen(boardMember: TilinpaatosHallitusMultiRoleUser, role: CustomerHallituksenJasenRole): HallituksenJasenLomake {
    const jasen: HallituksenJasenLomake = {
      name: boardMember.name,
      role: role,
      lemonaidUid: boardMember.lemonaidUid ?? null,
    }

    // group.get('selected').valueChanges.pipe(
    //   takeUntil(this._ngUnsubscribe)
    // ).subscribe(val => {
    //   if (!val) {
    //     const idx = this._selectedSigners.findIndex(signer => boardMember.lemonaidUid === signer.lemonaidUid)
    //     this._selectedSigners.splice(idx, 1)
    //   } else {

    //     // Double-check that the signer hasn't been added to the array yet
    //     const signerNotYetCached = this._selectedSigners.findIndex(signer => boardMember.lemonaidUid === signer.lemonaidUid) === -1
    //     if (signerNotYetCached) {
    //       this._selectedSigners.push(boardMember)
    //     }
    //   }
    // })

    return jasen
  }

  async saveSignature() {
    if (await this._kayttajaService.kayttajaaImpersonoidaan()) {
      return
    }

    if (!!this._saveStarted) {
      return
    }

    const isSure = await firstValueFrom(this._dialog.open(TilinpaatosAreYouSureDialog).afterClosed())
    if (!isSure) {
      return
    }

    this._saveStarted = true

    try {
      this._ladataanService.aloitaLataaminen()

      const metadataPromise = firstValueFrom(this._tilinpaatosMetadataObservable)
      const selectedTilikausiPromise = firstValueFrom(this.selectedTilikausiObservable)
      const signaturesDataPromise = firstValueFrom(this._tilinpaatosSigningDataObservable)

      const [metadata, selectedTilikausi, signaturesData] = await Promise.all([metadataPromise, selectedTilikausiPromise, signaturesDataPromise])

      const request: SignTilinpaatosRequest = {
        tilinpaatosAvain: metadata.viimeisinTilinpaatosVersioAvain,
        tilikausiAvain: selectedTilikausi.avain,
      }

      const resp = await this._firebaseLemonaid.functionsCall<SignTilinpaatosRequest, SignTilinpaatosResponse>('tilinpaatosSigning', request)
      if (!resp || resp.e) {
        this._errorHandler.handleError('Tilinpäätös signing failed! ' + (resp?.e || 'No response'))
        this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe'))
      } else {
        const isLastSigner = signaturesData.signs.filter(signer => !signer.signedAt).length <= 1
        const dialogData: TilinpaatosSignedDialogData = {
          tilintarkastetaan: metadata.tilintarkastetaan,
          isLastSigner: isLastSigner,
        }
        this._dialog.open(TilinpaatosSignedDialog, { data: dialogData })
      }
    } catch (err: any) {
      this._errorHandler.handleError(err)
      this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe'))
    } finally {
      this._ladataanService.lopetaLataaminen()
      this._saveStarted = false
    }
  }

  async approveSigners() {
    if (!this.lomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    if (!!this._saveStarted) {
      return
    }

    try {
      const selectedSigners = this._getSelectedSignersFromBoardMembers([...this.lomake.boardMembers])
      if (!selectedSigners?.length) {
        this.commonError.set(this._translationService.lokalisoi('reports-lemonaid.tilinpaatos-no-signers-error'))
        return
      }
      this._saveStarted = true

      this._ladataanService.aloitaLataaminen()

      const metadata = await firstValueFrom(this._tilinpaatosMetadataObservable)

      if (!metadata) {
        throw new Error('Cannot save tilinpäätös signers, no metadata found.')
      }

      const boardMinutesDate: NumberDate = this.lomake.boardMinutesDate().value() ? this._dateService.dateToNumber(this.lomake.boardMinutesDate().value()) : null
      const boardMinutesNumber: string = this.lomake.boardMinutesNumber().value()

      const request: SaveTilinpaatosSignersRequest = {
        tilinpaatosAvain: metadata.viimeisinTilinpaatosVersioAvain,
        selectedSigners: selectedSigners,
        boardMinutesDate: boardMinutesDate,
        boardMinutesNumber: boardMinutesNumber,
      }

      const resp = await this._firebaseLemonaid.functionsCall<SaveTilinpaatosSignersRequest, SaveTilinpaatosSignersResponse>('tilinpaatosSaveSigners', request)
      if (!resp || resp.e) {
        this._errorHandler.handleError('Tilinpäätös signers save failed! ' + (resp?.e || 'No response'))
        this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe'))
      } else {
        this._editingSignersSubject.next('view')
      }
    } catch (err: any) {
      this._errorHandler.handleError(err)
      this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe'))
    } finally {
      this._ladataanService.lopetaLataaminen()
      this._saveStarted = false
    }
  }

  async downloadPdf(type: 'tilinpaatos' | 'poytakirja') {
    this._ladataanService.aloitaLataaminen()

    try {
      const base64FilePromise = type === 'tilinpaatos' ? firstValueFrom(this._tilinpaatosPdfAsBase64Observable) : firstValueFrom(this._hallituksenPoytakirjaPdfObservable)
      const asiakasPromise = firstValueFrom(this._kayttajaService.nykyinenAsiakasObservable)
      const tilikausiPromise = firstValueFrom(this.selectedTilikausiObservable)
      const signingDataPromise = type === 'poytakirja' ? firstValueFrom(this._tilinpaatosSigningDataObservable) : Promise.resolve(null)

      const [base64File, asiakas, tilikausi, signingData] = await Promise.all([base64FilePromise, asiakasPromise, tilikausiPromise, signingDataPromise])

      if (!base64File?.base64) {
        return
      }

      let fileName = null
      if (type === 'tilinpaatos') {
        fileName = asiakas.nimi + ' - tilinpäätös - ' + tilikausi.loppuu.year + '.pdf'
      } else if (type === 'poytakirja') {
        fileName = asiakas.nimi + ' hallituksen pöytäkirja' + ' ' + signingData.boardMinutesNumber + '.pdf'
      }

      this._fileSaverService.saveBase64As(base64File.base64, fileName, 'pdf')
    } catch (err: any) {
      this._errorHandler.handleError(err)
      this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe'))
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  private _getSelectedSignersFromBoardMembers(jasenet: FieldTree<HallituksenJasenLomake>[]): TilinpaatosHallitusMultiRoleUser[] {
    const output: TilinpaatosHallitusMultiRoleUser[] = []
    for (const jasen of jasenet) {
      if (jasen().disabled()) {
        continue
      }

      const { name, lemonaidUid, role } = jasen().value()

      const addAsSigner = !!lemonaidUid && role !== 'varajasen'
      if (addAsSigner) {
        const alreadyAdded = output.find(added => added.lemonaidUid === lemonaidUid)
        if (alreadyAdded) {
          const selectedRole = role
          if (!alreadyAdded.roles.includes(selectedRole)) {
            alreadyAdded.roles.push(selectedRole)
          }
        } else {
          const user: TilinpaatosHallitusMultiRoleUser = {
            lemonaidUid: lemonaidUid,
            name: name,
            roles: [role],
          }
          output.push(user)
        }
      }
    }
    return output
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }
}
